/*
 * PRD-037 — Capa de datos del paso de plan del claim (app SEPARADA del panel).
 *
 * El portal del partner NO monta Redux ni el AuthContext del panel, así que los hooks del panel
 * (useAvailableSubscriptions, useActivatePlan) NO funcionan aquí: usan useSelector / useAuth y
 * crashearían sin sus providers. En su lugar replicamos sus DOS llamadas contra los MISMOS
 * endpoints usando axiosInstance directo: su interceptor de request inyecta el JWT de comercio +
 * el header id_comercio leyéndolos de localStorage (ver axiosInstance.ts) — justo lo que persiste
 * ClaimPage tras el claim. Así el authorizer del gateway recibe los headers que espera, sin
 * arrastrar los providers del panel ni romper el patrón de app separada.
 *
 * Reusamos los CONTRATOS de tipo del panel (import type, cero runtime) para no duplicar la forma:
 * - AvailableSubscription: lo que devuelve GET /v1/planes.
 * - ActivatePlanResponse / MetodoPago: el contrato de convertFreemium (FR-28).
 */
import axiosInstance from '@services/axiosInstance';
import type { AvailableSubscription } from '@interfaces/general';
import {
  normalizeActivateResponse,
  type ActivatePlanResponse,
  type MetodoPago,
  type RawActivatePlanResponse,
} from '@pages/Subscriptionsv2/hooks/useActivatePlan';

// GET /v1/planes — mismos planes públicos (is_public) que muestra el panel (useAvailableSubscriptions).
export const fetchPublicPlans = async (): Promise<AvailableSubscription[]> => {
  const { data } = await axiosInstance.get<AvailableSubscription[]>('/v1/planes');
  return data;
};

// Cuerpo del POST a convertFreemium. `coupon` es opcional: SOLO se envía si el dueño tecleó un
// código de descuento. El campo lleva el STRING tal cual (contrato acordado con el backend, que lo
// resuelve vía promotion_codes.list y lo aplica; si no existe, el pago sigue a precio lleno). El FE
// NO valida el código.
interface ConvertFreemiumRequest {
  plan_id: string;
  metodo_pago: MetodoPago;
  coupon?: string;
}

// POST /v1/suscriptions/convertFreemium — el dueño recién reclamado es FREEMIUM, así que SIEMPRE
// es la rama convertFreemium de useActivatePlan (nunca createSubscription). El backend responde
// un envoltorio de tracking de steps { message, completed_steps, data: { client_secret |
// hosted_invoice_url } } — normalizamos con la MISMA función que useActivatePlan (no duplicar
// el unwrap) para no repetir el bug de leer client_secret en el nivel equivocado.
export const convertFreemiumToPlan = async (
  planId: string,
  metodoPago: MetodoPago,
  coupon?: string,
): Promise<ActivatePlanResponse> => {
  const body: ConvertFreemiumRequest = {
    plan_id: planId,
    metodo_pago: metodoPago,
  };
  // Solo incluir el campo cuando el usuario escribió algo (tras trim). Si va vacío, no se manda.
  const trimmedCoupon = coupon?.trim();
  if (trimmedCoupon) body.coupon = trimmedCoupon;

  const { data } = await axiosInstance.post<RawActivatePlanResponse>('/v1/suscriptions/convertFreemium', body);
  return normalizeActivateResponse(data);
};

// Respuesta del preview de cupón. Contrato compartido con el proxy panel-comercio y el backend
// (POST /v1/suscriptions/validate-coupon): resuelve el promotion_code vía promotionCodes.list y,
// si aplica al producto del plan, calcula el precio con descuento. Es SOLO LECTURA — no crea
// suscripción ni cobra. Los montos (`amount_off`, `original_price`, `discounted_price`) vienen en
// CENTAVOS MXN; el FE los convierte a pesos para formatToCurrency.
export interface ValidateCouponResponse {
  valid: boolean; // el promotion_code existe y está activo
  applies_to_plan: boolean; // true si el coupon aplica al producto del plan (o no tiene applies_to)
  type: 'percent' | 'amount' | null;
  percent_off: number | null; // ej. 50
  amount_off: number | null; // en centavos MXN
  original_price: number | null; // precio del plan en centavos
  discounted_price: number | null; // precio final tras descuento en centavos
  message: string; // texto humano listo para mostrar inline
}

// POST /v1/suscriptions/validate-coupon — PREVIEW del cupón antes de crear la suscripción. Usa el
// MISMO axiosInstance que convertFreemiumToPlan, así que hereda el JWT de comercio + el header
// id_comercio de su interceptor de request (igual que el resto del claim). El proxy inyecta lo que
// el backend necesite, como en convertFreemium. Al ser preview, el dueño puede probar códigos sin
// generar suscripciones incomplete en Stripe: la sub se crea UNA vez al continuar al pago.
export const validateCoupon = async (code: string, planId: string): Promise<ValidateCouponResponse> => {
  const { data } = await axiosInstance.post<ValidateCouponResponse>('/v1/suscriptions/validate-coupon', {
    code: code.trim(),
    plan_id: planId,
  });
  return data;
};
